import React from 'react';
import type {
  LocationSuggestOption,
  AutocompleteOptionToListNodeAttributes,
  AutocompleteMapOptionsToListboxNodeAttributes,
  MultiAutocompleteOptionToListNodeAttributes,
  MultiLocationSuggestOption,
} from '@construct-kit/core';
import { LocationMd, Strong, P, ListboxOption, spacingHelper, SearchMd } from '@construct-kit/core';
import styled from 'styled-components';

const StyledListboxOption = styled(ListboxOption)`
  display: flex;
  align-items: center;
  svg {
    position: relative;
    top: -0.0625rem;
    margin-right: ${spacingHelper('extraSmall')};
    flex-shrink: 0;
  }
  p {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  div {
    display: flex;
    align-items: center;
  }
`;

const ListboxWrapper = styled.div`
  padding: ${spacingHelper('extraSmall')};
`;

export const customMapOptionToListNode = ({
  option,
  inputValue,
  isActive,
  mapOptionToString,
}: Omit<
  MultiAutocompleteOptionToListNodeAttributes | AutocompleteOptionToListNodeAttributes,
  'option'
> & {
  option: MultiLocationSuggestOption | LocationSuggestOption;
}) => {
  const optionText = mapOptionToString(option);
  const inputStart = optionText.toLowerCase().indexOf(inputValue.toLowerCase());
  const containsInputValue = inputValue && inputStart !== -1;

  const icon = option.type === 'recent' ? <SearchMd /> : <LocationMd />;

  return (
    <StyledListboxOption isActive={isActive} sizeVariant="large">
      {icon}
      <P variant="body01">
        {containsInputValue ? (
          <>
            {optionText.substring(0, inputStart)}
            <Strong>{optionText.substring(inputStart, inputStart + inputValue.length)}</Strong>
            {optionText.substring(inputStart + inputValue.length)}
          </>
        ) : (
          <span>{optionText}</span>
        )}
      </P>
    </StyledListboxOption>
  );
};

export const mapOptionsToListboxNode = ({
  options,
  sizeVariant,
  inputValue,
  mapOptionToString,
  mapOptionToListNode,
  getItemProps,
  highlightedIndex,
}: AutocompleteMapOptionsToListboxNodeAttributes) => {
  if (!options.length) return <ListboxWrapper>No results</ListboxWrapper>;
  const foundOptions = options.map((option, index) => ({ ...option, index }));
  return (
    <ListboxWrapper>
      <P id="recent-location-heading" variant="subtitle02">
        {' '}
        RECENT LOCATIONS
      </P>
      <P id="location-suggestions-heading" variant="subtitle02">
        {' '}
        LOCATION SUGGESTIONS
      </P>
      {foundOptions.map((option) =>
        React.cloneElement(
          mapOptionToListNode({
            option,
            sizeVariant,
            inputValue,
            mapOptionToString,
            isActive: highlightedIndex === option.index,
          }),
          getItemProps({
            item: option,
            index: option.index,
          }),
        ),
      )}
    </ListboxWrapper>
  );
};
